import { type CreateTripItemData } from 'tripPlanner/api/tripItem/types'
import type { components } from '@luxuryescapes/contract-trip/dist/server'

type Place = components['schemas']['place']

interface TripItemFields {
  itemType: string
  name?: string
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  flightNumber?: string
  airlineName?: string
  notes?: string
}

interface PlaceFields {
  from?: Place
  to?: Place
  originAirportCode?: string
  destinationAirportCode?: string
}

const commonFields = (fields: TripItemFields) => ({
  startDate: fields.startDate,
  endDate: fields.endDate,
  startTime: fields.startTime,
  endTime: fields.endTime,
  isBooked: false,
  sourceType: 'NA' as const,
})

/**
 * Core builder that creates trip items from parsed fields.
 * Handles all item type logic in one place.
 *
 * @param fields - Common fields like itemType, dates, times, name
 * @param places - Place-specific fields (from, to, airport codes)
 * @returns CreateTripItemData or undefined if required fields are missing
 */
export function buildTripItemFromFields(
  fields: TripItemFields,
  places: PlaceFields,
): CreateTripItemData | undefined {
  const common = commonFields(fields)
  const name = fields.name || 'Untitled'

  switch (fields.itemType) {
    case 'THING_TO_DO': {
      if (!places.to) return undefined
      return {
        type: 'THING_TO_DO',
        name,
        startPlace: places.to,
        endPlace: places.to,
        ...common,
      }
    }

    case 'ATTRACTION': {
      if (!places.to) return undefined
      return { type: 'ATTRACTION', name, place: places.to, ...common }
    }

    case 'EVENT': {
      if (!places.to) return undefined
      return { type: 'EVENT', name, place: places.to, ...common }
    }

    case 'RESTAURANT_BAR': {
      if (!places.to) return undefined
      return { type: 'RESTAURANT_BAR', name, place: places.to, ...common }
    }

    case 'ACCOMMODATION': {
      if (!places.to) return undefined
      return { type: 'ACCOMMODATION', place: places.to, ...common }
    }

    case 'CRUISE':
    case 'TOUR': {
      if (!places.from || !places.to) return undefined
      return {
        type: fields.itemType,
        name,
        startPlace: places.from,
        endPlace: places.to,
        ...common,
      }
    }

    case 'FLIGHT': {
      if (!places.from || !places.to || !places.originAirportCode || !places.destinationAirportCode) {
        return undefined
      }
      return {
        type: 'FLIGHT',
        startPlace: places.from,
        endPlace: places.to,
        originAirportCode: places.originAirportCode,
        destinationAirportCode: places.destinationAirportCode,
        flightNumber: fields.flightNumber,
        airlineName: fields.airlineName,
        ...common,
      }
    }

    case 'TRAIN':
    case 'BUS':
    case 'FERRY':
    case 'CAR_RENTAL':
    case 'OTHER_TRANSPORT': {
      if (!places.from || !places.to) return undefined
      return {
        type: fields.itemType,
        startPlace: places.from,
        endPlace: places.to,
        ...common,
      }
    }

    case 'NOTE': {
      return {
        type: 'NOTE',
        notes: fields.notes || name,
        ...common,
      }
    }

    default:
      return undefined
  }
}
